import React, { Suspense, useEffect, useMemo, useState } from 'react';
import {
    Outlet, useLocation, useNavigate, useParams,
} from 'react-router-dom';
import {
    faGamepad,
    faSliders,
    faPlug,
    faBookOpen,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useDispatch } from 'react-redux';

import { UserAuth } from '../../../../authentication/AuthContext.jsx';
import { FormHeader } from '../../../../components/page-header/FormHeader.jsx';
import SectionHeader from '../../../../components/section-header/SectionHeader.jsx';
import { getGameRegistryByGameClientId } from '../../../../services/admin-settings/GameService.jsx';
import { updateDashboardContext } from '../../../../redux-store/ReduxStore.js';
import LoadingPage from '../../../../pages/error-pages/LoadingPage.jsx';
import usePageLoader from '../../../../hooks/usePageLoader.js';
import useSelectedGameLifecycle from '../../../../hooks/useSelectedGameLifecycle.js';

import './GameWorkspace.css';

// Tabs migrated to the headerless-first-load pattern: while such a tab first-loads, the shell
// FormHeader + section band are hidden and ONE loader fills the content area, so the chrome reveals
// WITH the tab content (like every other page). Tabs not listed keep the chrome + their own loader.
const HEADERLESS_FIRST_LOAD_TABS = ['setup', 'knowledge', 'data-connections'];

// One row per tab — keeps labels, taglines, icons, and route builders
// in one place so the header tab strip and the section header below
// it stay in sync. Same shape Game User Management uses for its subtab
// table; the difference here is `to()` (URL-driven navigation) where
// GameUserManagement uses pure setState.
//
// `external: true` marks tabs that leave the workspace tree entirely
// (Knowledge currently routes to /live-ops/...). An external tab never
// reads as active, since once the user clicks it they unmount this
// layout — the visual "no tab is active in this strip" state is correct.
const TABS = [
    {
        id: 'setup',
        label: 'Setup',
        icon: faSliders,
        tagline: 'Per-build settings and game-level configuration.',
        to: (clientId) => `/admin-settings/game-mgmt/games/${clientId}/setup`,
    },
    {
        id: 'data-connections',
        label: 'Data Connections',
        icon: faPlug,
        tagline: 'External data sources feeding analytics for this game.',
        to: (clientId) => `/admin-settings/game-mgmt/games/${clientId}/data-connections`,
        // The DataConnector page carries its own header, so the shell's band is redundant here.
        hideSectionHeader: true,
    },
    {
        id: 'knowledge',
        label: 'Knowledge',
        icon: faBookOpen,
        tagline: 'Game knowledge base — uploaded, extracted, and authored docs powering AI for this game.',
        to: (clientId) => `/admin-settings/game-mgmt/games/${clientId}/knowledge`,
        // The Knowledge page renders its own "Knowledge Entries" section
        // band (which also carries the filter row), so the shell's band
        // would just duplicate it — suppress it for this tab only.
        hideSectionHeader: true,
    },
    // The "Edit" tab was removed — registry editing (game metadata, builds, regions) already lives
    // in Game Registration's edit flow, so duplicating it inside the workspace was redundant.
];

/**
 * Per-game workspace shell.
 *
 * <p>Mounted by `settingRoutes.jsx` at
 * `/admin-settings/game-mgmt/games/:gameClientId` — URLs are addressed
 * by the primary build's Client ID (e.g. `aurora_mobile`) rather than
 * the registry's Mongo `_id` so they read meaningfully when shared.
 * The actual surface (Setup, Data Connections, Edit) renders via the
 * child `<Outlet />`. The layout owns:
 *   - The one-time fetch of the registry by gameClientId (so children
 *     don't each re-fetch it).
 *   - The page header (game name + back-to-Workspace).
 *   - The tab strip linking to every subsurface.
 *
 * <p>Multi-build games: any build's clientId resolves the same
 * registry on the backend; the workspace stays per-game and surfaces
 * every build's settings inside. The URL displays whichever clientId
 * the user navigated in with; the picker always uses the primary
 * (first build's) clientId.
 *
 * <p>This is the Option B IA — see the comment block in
 * `settingRoutes.jsx` for the broader migration plan.
 */
const GameWorkspaceLayout = () => {
    // Route param is the URL-facing identifier (a Client ID). The
    // registry's Mongo _id is derived from the fetched DTO below
    // and exposed to children via outlet context for downstream
    // endpoints that need it (e.g. PATCH .../{gameRegistryId}/...).
    const { gameClientId: routeClientId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const { user } = UserAuth();

    // gameId is the registry's Mongo _id — looked up from the
    // gameClientId via the GET endpoint, then handed to children that
    // need it (Setup tab's per-build settings PATCH, Data Connections
    // page, Edit form, etc.).
    const [gameId, setGameId] = useState('');
    const [gameName, setGameName] = useState('');
    // Mirror of the URL's clientId — kept in state so it has the same
    // lifecycle as the other fields in outlet context. The URL is the
    // source of truth; this just makes downstream consumers cleaner.
    const [gameClientId, setGameClientId] = useState(routeClientId || '');
    // Display name of the client the URL's gameClientId points at (resolved from the registry's
    // builds) so the Setup tab can show "name — id" rather than a bare 8-digit id.
    const [gameClientName, setGameClientName] = useState('');
    // All clients (builds) of the current game — feeds the page-header client switcher so the
    // operator can move between clients of the same game without leaving the workspace.
    const [builds, setBuilds] = useState([]);

    // Resolve registry by the URL's Client ID. Bootstrap call for the
    // workspace — every nested tab depends on the gameId (Mongo _id)
    // for at least some of its backend writes. Failure stays silent
    // and the header falls back to the URL's clientId so the page
    // doesn't visibly break, just shows reduced affordances.
    useEffect(() => {
        if (!routeClientId || !user?.accessToken) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await getGameRegistryByGameClientId({
                    gameClientId: routeClientId,
                    accessToken: user.accessToken,
                });
                if (cancelled) return;
                const dto = res?.gameRegistryDTO || res?.game || {};
                setGameId(dto?.id || '');
                setGameName(dto?.gameName || '');
                const gameBuilds = dto?.builds || [];
                setBuilds(gameBuilds);
                const primaryBuild = gameBuilds.find(b => b.gameClientId === routeClientId);
                setGameClientName(primaryBuild?.clientName || '');

                // Sync the workspace's context up to the global top bar so it shows "org / game"
                // (matching Game User Management). The workspace is URL-addressed, so this one-way
                // URL→redux sync is what surfaces the game chip; switching the game there navigates
                // back here via a new URL, which re-runs this sync. Values are always valid for the
                // org, so DashboardHeader's auto-select reconciliation leaves them untouched.
                const ctxPatch = {
                    selectedGameId: dto?.id || '',
                    selectedGameName: dto?.gameName || '',
                    selectedGameClientId: routeClientId,
                };
                if (dto?.orgId) ctxPatch.selectedOrgId = dto.orgId;
                dispatch(updateDashboardContext(ctxPatch));
                // The URL's clientId is what the user navigated in
                // with — keep it as the displayed identifier rather
                // than forcing a switch to the primary build's id.
                // (Multi-build games can have multiple valid URLs;
                // honoring whichever one was opened avoids a confusing
                // redirect.)
                setGameClientId(routeClientId);
            } catch {
                /* header falls back to the URL's clientId */
            }
        })();
        return () => { cancelled = true; };
    }, [routeClientId, user?.accessToken]);

    // Active-tab highlighting comes from `location.pathname` rather
    // than NavLink's built-in `isActive` so the cross-tab (Knowledge ↗,
    // which points at a Live Ops URL outside this workspace) doesn't
    // accidentally read as active when the user is on it but the URL
    // doesn't match our tab prefix. Bare `/games/:gameClientId` is
    // redirected to `/setup` by the router, so the empty-tail case in
    // practice never reaches this code.
    const activeTab = useMemo(() => {
        const tail = location.pathname.split(`/games/${routeClientId}`)[1] || '';
        return tail.replace(/^\//, '').split('/')[0] || 'setup';
    }, [location.pathname, routeClientId]);

    // True only on a tab's own route (…/knowledge), false on its sub-pages
    // (…/knowledge/upload, /extract, /new, /drafts). Sub-pages are forms that render
    // immediately and don't report load state, so gating them would leave the loader
    // up forever on a direct load or refresh.
    const isTabIndexRoute = useMemo(() => {
        const tail = (location.pathname.split(`/games/${routeClientId}`)[1] || '').replace(/^\//, '');
        return tail.split('/').filter(Boolean).length <= 1;
    }, [location.pathname, routeClientId]);

    // Active tab metadata (label, icon, tagline) — drives the
    // SectionHeader band below the FormHeader so each subsurface gets
    // a contextual title and one-line summary, matching the Game User
    // Management pattern.
    const currentTab = useMemo(
        () => TABS.find(t => t.id === activeTab) || TABS[0],
        [activeTab],
    );

    // Headerless first-load for migrated tabs: the tab reports its raw load state up via
    // `onTabLoadingChange`; usePageLoader holds it so the loader can't flash. A frozen scope replaces
    // the tab with the lock card (the tab never mounts to report), so skip the gating there — otherwise
    // the loader would stick with no tab to clear it.
    const isHeaderlessTab = HEADERLESS_FIRST_LOAD_TABS.includes(currentTab.id) && isTabIndexRoute;
    const { frozen } = useSelectedGameLifecycle();
    const [tabRawLoading, setTabRawLoading] = useState(true);
    // Reset on tab change AND on a client/game switch (routeClientId): a workspace context switch keeps
    // the same activeTab but changes :gameClientId, and the Outlet is keyed by it (below) so the tab
    // remounts and refetches. Resetting here shows the loader immediately; the remounted tab clears it
    // when its fetch settles (usePageLoader holds it ≥500ms, so it never just flashes).
    // isTabIndexRoute is in the deps so returning from a sub-page re-arms the gate on the
    // same frame the tab remounts, instead of showing empty chrome for a tick first.
    useEffect(() => { setTabRawLoading(true); }, [activeTab, routeClientId, isTabIndexRoute]);
    const { showFirstLoad, showBody } =
        usePageLoader(isHeaderlessTab && !frozen ? tabRawLoading : false);
    // First load is headerless (chrome hidden, revealed with content); a later reload keeps the chrome
    // and shows the loader in the body — both matching every other page.
    const hideChrome = isHeaderlessTab && !frozen && showFirstLoad;
    const busy = isHeaderlessTab && !frozen && (showFirstLoad || showBody);

    // Switching between the 3 children (Setup / Data Sources / Knowledge) happens
    // through the title dropdown below — there is no subheader tab strip. All three
    // are available to anyone who can open the workspace; their backends are
    // per-build (ownsBuild), so a build co-owner only sees their own build's data.
    //
    // Title-dropdown contents: this workspace's 3 children (Setup / Data Sources /
    // Knowledge) as sisters — same pattern as Game Metadata (Client Versions /
    // Game Features). Built here rather than from the static nav tree because the
    // child routes carry the :gameClientId param; picking one navigates to that tab.
    const menuContext = useMemo(() => ({
        parentLabel: 'Game Workspace',
        grandparentLabel: 'Game Management',
        sisters: TABS.map(t => ({
            path: t.to(routeClientId),
            label: t.label,
            icon: <FontAwesomeIcon icon={t.icon} />,
        })),
        parentSisters: [],
    }), [routeClientId]);
    const menuCurrentPath = currentTab.to(routeClientId);

    // Tab pages can inject their own CTAs (Refresh, Add Connection,
    // etc.) into the FormHeader via outlet context. The child calls
    // `setHeaderExtras(jsx)` on mount and `setHeaderExtras(null)` on
    // unmount. Saves each tab from rendering its own redundant header
    // strip below the workspace's FormHeader.
    const [headerExtras, setHeaderExtras] = useState(null);
    // A detail view inside a tab (an open connection, or the Add-connection form)
    // drives the shell FormHeader through these overrides: a trailing "/ formTitle"
    // segment, a replacement subheader tab strip, and a Back that returns within the
    // tab (to the connections list) instead of leaving the workspace. Null ⇒ the
    // tab's default header (current-child dropdown, no subheader, Back → Game Mgmt).
    const [headerFormTitle, setHeaderFormTitle] = useState(null);
    const [headerTabs, setHeaderTabs] = useState(null); // { tabs, activeTab, onTabChange } | null
    const [headerBack, setHeaderBack] = useState(null); // () => void | null

    return (
        <>
            {/* Back exits the workspace up to Game Management. The title dropdown
                lists this workspace's 3 children (see menuContext above); the build
                (client) lives in the top context-bar switcher, so the breadcrumb
                doesn't repeat it. */}
            {/* Migrated tabs hide the shell chrome during first-load so it reveals WITH the tab content
                (like every other page); non-migrated tabs keep it always present. */}
            {!hideChrome && (
                <FormHeader
                    onBack={headerBack || (() => navigate('/admin-settings/game-mgmt/games'))}
                    parentTitle={currentTab.label}
                    parentPagePath="/admin-settings/game-mgmt/workspace"
                    formTitle={headerFormTitle || undefined}
                    menuContext={menuContext}
                    menuCurrentPath={menuCurrentPath}
                    tabs={headerTabs?.tabs}
                    activeTab={headerTabs?.activeTab}
                    onTabChange={headerTabs?.onTabChange}
                    actions={headerExtras}
                />
            )}

            <div className="content-body">
                <div className="gw-body">
                    {/* SectionHeader band — icon + title (current tab
                        label) + per-tab tagline. Mirrors the chrome
                        used on Game User Management so the workspace
                        reads as a sibling of the rest of game-settings.
                        Tabs whose page renders its own section band
                        (e.g. Knowledge) opt out via `hideSectionHeader`
                        so the two don't stack. */}
                    {!hideChrome && !currentTab.hideSectionHeader && (
                        <SectionHeader
                            icon={currentTab.icon || faGamepad}
                            title={currentTab.label.replace(' ↗', '')}
                            tagline={currentTab.tagline}
                        />
                    )}

                    <div className="gw-tab-panel">
                        {/* One headerless loader for a migrated tab's first-load (chrome is hidden above),
                            so it fills the content area and reveals chrome + content together. */}
                        {busy && <LoadingPage />}
                        {/* Tab content — kept mounted (fetches + reports) but hidden while `busy`.
                            Own Suspense so a tab's chunk loads INSIDE the panel, where its first-load
                            loader also sits, so there's no jump. */}
                        <div className="gw-tab-panel-inner" style={busy ? { display: 'none' } : undefined}>
                            <Suspense fallback={busy ? null : <LoadingPage />}>
                                {/* Keyed by the client id so a game/build switch (same tab, new
                                    :gameClientId) remounts the tab → it refetches for the new scope and
                                    re-runs its load → loader, instead of silently re-rendering stale. */}
                                <Outlet key={routeClientId} context={{
                                    gameId, gameName, gameClientId, gameClientName,
                                    setHeaderExtras, setHeaderFormTitle, setHeaderTabs, setHeaderBack,
                                    onTabLoadingChange: isHeaderlessTab ? setTabRawLoading : undefined,
                                }} />
                            </Suspense>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default GameWorkspaceLayout;
